import { useSyncExternalStore } from 'react'

/**
 * App theme (light / dark). The whole palette is CSS custom properties
 * (styles/index.css) that every Tailwind color resolves through, so
 * flipping `data-theme` on <html> re-themes the entire tree with no
 * per-component work. A pre-paint script in index.html applies the saved
 * (or OS-preferred) theme before React mounts so there is no flash; this
 * module is the runtime source of truth for reading/toggling it.
 *
 * Stone-image wells intentionally stay light in both themes (supplier
 * photos are shot on white/grey) — see StoneImage's explicit bg-white.
 */
export type Theme = 'light' | 'dark'

const KEY = 'diaex-theme'
const listeners = new Set<() => void>()

/**
 * The user's explicit choice if any, else light. Dark mode is opt-in for
 * now (not auto-following OS) so shipping it changes nothing for anyone
 * until they flip the switch — once the night register is fully QA'd across
 * every view we can switch the default to `prefers-color-scheme`.
 */
export function getTheme(): Theme {
  try {
    const s = localStorage.getItem(KEY)
    if (s === 'light' || s === 'dark') return s
  } catch { /* private mode / SSR */ }
  return 'light'
}

/** Apply + persist a theme, update the address-bar color, notify hooks. */
export function setTheme(t: Theme): void {
  try { localStorage.setItem(KEY, t) } catch { /* ignore */ }
  document.documentElement.setAttribute('data-theme', t)
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', t === 'dark' ? '#0e0f12' : '#f7f8fa')
  listeners.forEach((fn) => fn())
}

export function toggleTheme(): void {
  setTheme(getTheme() === 'dark' ? 'light' : 'dark')
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

/** Reactive current theme for toggle controls. */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, getTheme, () => 'light')
}
